import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import React, { useState } from 'react'

interface AmendmentConfigFormProps {
  title: string
  changeType: 'minor' | 'patch'
  dealPK: string
  dealSK: string
  dealName: string
  currentVersion: string
  deals: any[]
  onSubmit: (message: string) => void
  isInteractive: boolean
}

const AmendmentConfigForm: React.FC<AmendmentConfigFormProps> = ({
  title,
  changeType,
  dealPK,
  dealSK,
  dealName,
  currentVersion,
  deals,
  onSubmit,
  isInteractive,
}) => {
  // Look up current deal dates from cache
  const currentDeal = deals.find(d => d?.PK === dealPK && d?.SK === dealSK)

  const [startDate, setStartDate] = useState<Dayjs | null>(
    currentDeal?.startDate ? dayjs(currentDeal.startDate) : null
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(
    currentDeal?.endDate ? dayjs(currentDeal.endDate) : null
  )
  const [needsNewContract, setNeedsNewContract] = useState(false)
  const [syncProductDates, setSyncProductDates] = useState(true)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = () => {
    if (changeType === 'minor' && (!startDate || !endDate)) return

    if (changeType === 'minor') {
      const lines = [
        `Configure minor amendment for "${dealName}" (PK: ${dealPK}, SK: ${dealSK}, version: ${currentVersion}):`,
        `- New start date: ${startDate!.format('YYYY-MM-DD')}`,
        `- New end date: ${endDate!.format('YYYY-MM-DD')}`,
        `- Needs new contract: ${needsNewContract ? 'Yes' : 'No'}`,
        `- Sync product dates to deal dates: ${syncProductDates ? 'Yes' : 'No'}`,
        'Please proceed with this minor version update.',
      ]
      setSubmitted(true)
      onSubmit(lines.join('\n'))
    } else {
      const message = `Proceed with patch amendment for "${dealName}" (PK: ${dealPK}, SK: ${dealSK}, version: ${currentVersion}). Please walk me through the pricing changes.`
      setSubmitted(true)
      onSubmit(message)
    }
  }

  // Read-only / submitted state
  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 400,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted ? `${changeType === 'minor' ? 'Renewal' : 'Patch'} config submitted` : title}
        </Typography>
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 1.5,
          py: 1.5,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 400,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {title}
        </Typography>

        {changeType === 'minor' && (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        fontSize: '0.8rem',
                        bgcolor: 'background.default',
                      },
                    },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                minDate={startDate || undefined}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        fontSize: '0.8rem',
                        bgcolor: 'background.default',
                      },
                    },
                  },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={needsNewContract}
                    onChange={(_, checked) => setNeedsNewContract(checked)}
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Requires new contract</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={syncProductDates}
                    onChange={(_, checked) => setSyncProductDates(checked)}
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Sync product dates to deal dates</Typography>}
              />
            </Box>
          </>
        )}

        {changeType === 'patch' && (
          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            The agent will walk you through pricing changes for each product.
          </Typography>
        )}

        <Button
          size="small"
          variant="outlined"
          onClick={handleSubmit}
          disabled={changeType === 'minor' && (!startDate || !endDate)}
          sx={{
            alignSelf: 'flex-end',
            fontWeight: 500,
            fontSize: '0.75rem',
            borderColor: 'text.ai_agent_text',
            color: 'text.ai_agent_text',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              borderColor: 'text.highlight',
              color: 'text.primary',
            },
          }}
        >
          {changeType === 'minor' ? 'Configure Renewal' : 'Proceed'}
        </Button>
      </Box>
    </LocalizationProvider>
  )
}

export default AmendmentConfigForm
